// The Display creative builder of the creative builder: banner groups, each a
// click-through and any number of banners in the catalog's sizes. Split out of
// creative.tsx, which still exports it.

import { useEffect, useId, useRef, useState } from 'react';
import type { ChangeEvent, Dispatch, SetStateAction } from 'react';
import { useCreatives } from '@/api/hooks';
import type { Creative } from '@/api/types';
import { RequiredField } from '@/components/required-field';
import { libraryToAssets } from '@/lib/creative-library-assets';
import { LibraryPicker } from './library-picker';
import { PinnedVersionBadge } from '@/components/pinned-version-badge';
import {
  DISPLAY_ACCEPT,
  DISPLAY_ANIMATION_MAX_MS,
  DISPLAY_FORMATS_TEXT,
  formatDisplayKb,
  validateDisplayFile,
} from '@/lib/media-validation/display';
import { DisplayAnimationNote } from '@/components/display-animation-note';
import {
  dspAssetFieldKey,
  dspDestinationFieldKey,
  type CreativeBuilderProduction,
  type CreativeSlot,
  type DspSlotData,
} from '@/lib/creative-required-issues';
import type { ChannelConfig } from './creative-config';
import { defaultAdName, type CreativeRequiredFeedback } from './creative-meta-editor';
import type { CreativeRolloutBadgeProps } from './creative-ctv-builder';

export interface DspGroupBuilderProps {
  /** The Display channel config: its size catalog and byte limit. */
  cfg: { dsp: NonNullable<ChannelConfig['dsp']> };
  production: CreativeBuilderProduction | null | undefined;
  /** The banner groups. Without them the builder keeps its own. */
  groups?: CreativeSlot[];
  setGroups?: Dispatch<SetStateAction<CreativeSlot[]>>;
  /** Each group's destination and banners, by group id. Without them the builder keeps its own. */
  data?: Record<string, DspSlotData>;
  setData?: Dispatch<SetStateAction<Record<string, DspSlotData>>>;
  singleMode?: boolean;
  availableCount?: number;
  requiredFeedback?: CreativeRequiredFeedback;
  locked?: boolean;
  rolloutFor?: (position: number) => CreativeRolloutBadgeProps;
}

export function DspGroupBuilder({ cfg, production, groups: groupsProp, setGroups: setGroupsProp, data: dataProp, setData: setDataProp, singleMode, availableCount = 0, requiredFeedback, locked = false, rolloutFor = () => ({}) }: DspGroupBuilderProps) {
  const adNameId = useId();
  const { data: libraryCreatives = [] } = useCreatives();
  const [groupsInner, setGroupsInner] = useState<CreativeSlot[]>([{ id: 'dg-1', label: 'Banner Group 1' }]);
  const [dataInner, setDataInner] = useState<Record<string, DspSlotData>>({ 'dg-1': { destUrl: '', banners: [] } });
  const groups = groupsProp || groupsInner;
  const setGroups = setGroupsProp || setGroupsInner;
  const data = dataProp || dataInner;
  const setData = setDataProp || setDataInner;
  const [activeId, setActiveId] = useState(groups[0]?.id || 'dg-1');
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [validating, setValidating] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const pendingForRef = useRef<string | null>(null);

  const update = (id: string, patch: Partial<DspSlotData>) => setData(d => ({ ...d, [id]: { ...(d[id] || {destUrl:'',banners:[]}), ...patch } }));
  const cur: DspSlotData = data[activeId] || { destUrl: '', banners: [] };
  const c = cfg.dsp;
  const destinationRequiredKey = dspDestinationFieldKey(activeId);
  const assetRequiredKey = dspAssetFieldKey(activeId);

  useEffect(() => {
    const firstInvalid = groups.find((group) =>
      requiredFeedback?.messages?.[dspDestinationFieldKey(group.id)]
      || requiredFeedback?.messages?.[dspAssetFieldKey(group.id)]);
    if (firstInvalid && firstInvalid.id !== activeId) {
      setActiveId(firstInvalid.id);
      setErrMsg(null);
    }
  }, [requiredFeedback?.attempt]);

  const addGroup = () => {
    const id = `dg-${Date.now()}`;
    setGroups([...groups, { id, label: `Banner Group ${groups.length + 1}` }]);
    setData({ ...data, [id]: { destUrl: '', banners: [] } });
    setActiveId(id);
    setErrMsg(null);
  };
  const removeGroup = (id: string) => {
    if (groups.length <= 1) return;
    const next = groups.filter(g => g.id !== id);
    setGroups(next);
    setData(d => {
      d[id]?.banners?.forEach(b => b.url && URL.revokeObjectURL(b.url));
      const { [id]: _, ...rest } = d;
      return rest;
    });
    const first = next[0];
    if (activeId === id && first) setActiveId(first.id);
  };

  const openPicker = () => {
    setErrMsg(null);
    pendingForRef.current = activeId;
    fileInput.current?.click();
  };

  const handleFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    const targetId = pendingForRef.current || activeId;
    setErrMsg(null);
    setValidating(true);
    try {
      // Type, bytes and size are judged from the file's own bytes, as attach
      // judges them; `naturalWidth` let a renamed WebP or a rotated JPEG through.
      const check = await validateDisplayFile(file, null, { sizesListed: true });
      const image = check.image;
      const match = image
        ? c.sizes.find(s => s.w === image.width && s.h === image.height) || check.size
        : null;
      // No refusal means the bytes were read and matched a catalog size, so
      // `image` and `match` are set; the second half only satisfies the types.
      if (check.refusals.length || !image || !match) {
        setErrMsg(check.refusals.join(' '));
        return;
      }
      const probe = { w: image.width, h: image.height, url: URL.createObjectURL(file) };
      setData(prev => {
        const t = prev[targetId] || { destUrl: '', banners: [] };
        return {
          ...prev,
          [targetId]: {
            ...t,
            banners: [
              ...t.banners,
              {
                id: `b-${Date.now()}`,
                file,
                type: 'image',
                name: file.name,
                url: probe.url,
                size: match,
                width: probe.w,
                height: probe.h,
                w: probe.w,
                h: probe.h,
                // Shown as a warning on the tile and in review; never a refusal.
                animation: image.animation,
              },
            ],
          },
        };
      });
    } catch (err) {
      setErrMsg(err instanceof Error ? err.message : String(err));
    } finally {
      setValidating(false);
    }
  };

  const removeBanner = (bid: string) => {
    setData(prev => {
      const t = prev[activeId];
      if (!t) return prev;
      const target = t.banners.find(b => b.id === bid);
      if (target?.url) URL.revokeObjectURL(target.url);
      return { ...prev, [activeId]: { ...t, banners: t.banners.filter(b => b.id !== bid) } };
    });
  };

  // The group being edited, and the library version it is pinned to.
  const activeGroup = groups.find(gg=>gg.id===activeId);
  const activePin = activeGroup?.fromLibrary;

  const counts = c.sizes.map(s => ({ ...s, n: cur.banners.filter(b => b.size.name === s.name).length }));

  return (
    <>
      {!singleMode && (
      <div className="creative-tabs">
        {groups.map(g => {
          const bannerCount = data[g.id]?.banners?.length ?? 0;
          return (
          <button key={g.id} className={`creative-tab ${activeId===g.id?'on':''}`} onClick={()=>{ setActiveId(g.id); setErrMsg(null); }}>
            <span>{g.adName || defaultAdName(production, g.label, 'dsp')}</span>
            {(bannerCount > 0) && <span style={{color:'var(--good)',fontSize:11,marginLeft:4}}>● {bannerCount}</span>}
            {groups.length > 1 && (
              <span className="creative-tab-x" onClick={(e)=>{ e.stopPropagation(); removeGroup(g.id); }} title="Remove this banner group">×</span>
            )}
          </button>
          );
        })}
        <button className="creative-tab creative-tab-add" onClick={addGroup} title="Add another banner group">
          <span style={{fontSize:14,lineHeight:1}}>+</span><span>Add banner group</span>
        </button>
        {/* Offered only with something to pick: it opened an empty picker (F5 UX P8). */}
        <button className="lib-pick-cta" disabled={availableCount === 0} onClick={()=>setPickerOpen(true)} title="Pick existing Display banner sets from the library" style={{marginLeft:'auto'}}>
          <span className="lib-pick-ico">⧉</span>
          {availableCount === 0
            ? <span>Library · no Display creatives yet</span>
            : <><span>Pick from library</span><span className="lib-pick-count">{availableCount} avail.</span></>}
        </button>
      </div>
      )}

      <LibraryPicker open={pickerOpen} channel="dsp" currentShow={production} multi onClose={()=>setPickerOpen(false)} onPick={(list: Creative[])=>{
        if (!list || !list.length) return;
        const newGroups = [...groups];
        const newData = { ...data };
        const nowTs = Date.now();

        // If the active banner group is empty (no banners), replace it with first pick.
        const activeIdx = newGroups.findIndex(g => g.id === activeId);
        const active = newGroups[activeIdx];
        const activeCur = active ? data[active.id] : null;
        const activeIsEmpty = !!active && (!activeCur?.banners || activeCur.banners.length === 0);

        let startIdx = 0;
        let firstInsertedId: string | null = null;
        const first = list[0];
        if (activeIsEmpty && first) {
          const cr = first;
          const oldId = active.id;
          const id = `dg-lib-${cr.id}-${nowTs}-0`;
          newGroups[activeIdx] = { id, label: active.label, adName: cr.name, fromLibrary: { id: cr.id, name: cr.name, revision: cr.approved_revision ?? cr.revision } };
          delete newData[oldId];
          newData[id] = libraryToAssets(cr, production, 'dsp');
          firstInsertedId = id;
          startIdx = 1;
        }

        for (let i = startIdx; i < list.length; i++) {
          const cr = list[i];
          if (!cr) continue;
          const id = `dg-lib-${cr.id}-${nowTs}-${i}`;
          newGroups.push({ id, label: `Banner Group ${newGroups.length + 1}`, adName: cr.name, fromLibrary: { id: cr.id, name: cr.name, revision: cr.approved_revision ?? cr.revision } });
          newData[id] = libraryToAssets(cr, production, 'dsp');
          if (!firstInsertedId) firstInsertedId = id;
        }

        setGroups(newGroups);
        setData(newData);
        setActiveId(firstInsertedId || newGroups[newGroups.length - 1]?.id || activeId);
      }}/>

      <input ref={fileInput} type="file" accept={DISPLAY_ACCEPT} style={{display:'none'}} onChange={handleFile}/>

      <div className="flex between items-end mb-2" style={{gap:10, flexWrap:'wrap'}}>
        <div>
          <div className="field-lbl" style={{margin:0}}>Banners in this group · {cur.banners.length}</div>
          <div className="muted" style={{fontSize:11,marginTop:2}}>{DISPLAY_FORMATS_TEXT} · the {c.sizes.length} sizes below · duplicates of the same size allowed · {formatDisplayKb(c.maxBytes)} max · animation {DISPLAY_ANIMATION_MAX_MS / 1000} s at most, then still</div>
        </div>
        <div className="flex gap-1 items-center" style={{flexWrap:'wrap'}}>
          {/* A size with no banner yet is an outline: dimming it (opacity .55) put its text below AA. */}
          {counts.map(s => <span key={s.name} className={`tag${s.n ? '' : ' tag-empty'}`}>{s.w}×{s.h} · {s.n}</span>)}
        </div>
      </div>

      <div className="facet" style={{margin:'0 0 12px', padding:'10px 12px'}}>
        <div className="facet-hd" style={{marginBottom:6}}>
          <h5 style={{fontSize:10}}><label htmlFor={adNameId}>Ad Name</label></h5>
          <span className="muted" style={{fontSize:10}}>Internal name — not shown to viewers{activePin ? <> · <span style={{color:'var(--accent)'}}>from library: {activePin.name}</span></> : null}</span>
          {activePin && (
            <PinnedVersionBadge
              creative={libraryCreatives.find(lc => lc.id === activePin.id)}
              pinnedRevision={activePin.revision}
              campaignLive={!!locked}
              {...rolloutFor(groups.findIndex(gg=>gg.id===activeId))}
            />
          )}
        </div>
        <input
          id={adNameId}
          type="text"
          value={activeGroup?.adName ?? defaultAdName(production, activeGroup?.label, 'dsp')}
          onChange={e=>setGroups(groups.map(gg => gg.id===activeId ? {...gg, adName: e.target.value} : gg))}
          className="input"
          style={{width:'100%', fontSize:12}}
        />
      </div>

      <RequiredField
        fieldKey={destinationRequiredKey}
        message={requiredFeedback?.messages?.[destinationRequiredKey]}
        attempt={requiredFeedback?.attempt ?? 0}
        surface
        className="facet"
        style={{margin:'0 0 12px', padding:'10px 12px'}}
      >
        <div className="facet-hd" style={{marginBottom:6}}>
          <h5 style={{fontSize:10}}>Destination URL <span style={{color:'var(--live)'}}>*</span></h5>
          <span className="muted" style={{fontSize:10}}>Required · click-through landing page for this banner group</span>
        </div>
        <input
          data-validation-focus
          type="url"
          value={cur.destUrl}
          onChange={e=>update(activeId, { destUrl: e.target.value })}
          placeholder="https://www.shubert.nyc/production/..."
          className="input"
          style={{width:'100%', fontFamily:'var(--font-mono)', fontSize:12}}
        />
      </RequiredField>

      <RequiredField
        fieldKey={assetRequiredKey}
        message={requiredFeedback?.messages?.[assetRequiredKey]}
        attempt={requiredFeedback?.attempt ?? 0}
        surface
      >
        <div className="grid-3" style={{gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))', gap:12, alignItems:'end'}}>
          {cur.banners.map(a => (
            <div key={a.id}>
              <div className="creative-asset" style={{aspectRatio:`${a.w}/${a.h}`, position:'relative', overflow:'hidden', maxHeight: 240}}>
                <img src={a.url} alt={a.name} style={{position:'absolute',inset:0,width:'100%',height:'100%',objectFit:'cover',display:'block'}}/>
                <div style={{position:'absolute',top:0,left:0,right:0,height:38,background:'linear-gradient(to bottom, rgba(0,0,0,.55), transparent)',pointerEvents:'none'}}/>
                <div style={{position:'absolute',bottom:0,left:0,right:0,padding:'14px 8px 6px',background:'linear-gradient(to top, rgba(0,0,0,.7), transparent)',pointerEvents:'none'}}>
                  <div style={{fontFamily:'var(--font-mono)',fontSize:9,color:'rgba(255,255,255,.85)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{a.name}</div>
                </div>
                <div style={{position:'absolute',top:5,left:7,fontFamily:'var(--font-mono)',fontSize:9.5,letterSpacing:'.08em',color:'#fff',textTransform:'uppercase',textShadow:'0 1px 2px rgba(0,0,0,.55)',fontWeight:700}}>{a.w}×{a.h}</div>
                <button className="asset-x" onClick={()=>removeBanner(a.id)} title="Remove banner">×</button>
              </div>
              <div style={{fontFamily:'var(--font-mono)',fontSize:10,color:'var(--ink-2)',marginTop:4,letterSpacing:'.05em'}}>{a.size.name}</div>
              <DisplayAnimationNote animation={a.animation} byteSize={a.file?.size ?? a.byteSize}/>
            </div>
          ))}
          <button data-validation-focus className="ctv-add" style={{aspectRatio:'4/3', maxHeight:160}} onClick={openPicker} disabled={validating}>
            <div style={{fontSize:28,lineHeight:1,marginBottom:4}}>+</div>
            <div style={{fontFamily:'var(--font-mono)',fontSize:10,letterSpacing:'.1em',textTransform:'uppercase'}}>{validating ? 'Validating…' : 'Upload Banner'}</div>
            <div className="muted" style={{fontSize:10,marginTop:4}}>{DISPLAY_FORMATS_TEXT} · {formatDisplayKb(c.maxBytes)} max</div>
          </button>
        </div>
      </RequiredField>

      {errMsg && (
        <div className="asset-err" style={{marginTop:10}}>
          <span style={{flexShrink:0}}>⚠</span>
          <span style={{flex:1}}>{errMsg}</span>
          <button className="asset-err-x" onClick={()=>setErrMsg(null)} title="Dismiss">×</button>
        </div>
      )}
    </>
  );
}
